import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Image, Video, FileText, Music, Film, FileImage, Plus } from 'lucide-react';
import axios from 'axios';
import { AdCreative } from '../types/ads.types';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface MediaUploaderProps {
  creatives: AdCreative[];
  onCreativesChange: (creatives: AdCreative[]) => void;
  acceptedTypes?: string[];
  maxFiles?: number;
  disabled?: boolean;
  platform?: string;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function MediaUploader({
  creatives = [],
  onCreativesChange,
  acceptedTypes = ['image/*', 'video/*', 'audio/*'],
  maxFiles = 10,
  disabled = false,
  platform = '',
  blobManager,
  contentId,
  apiBaseUrl = ''
}: MediaUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const blobUrlsRef = useRef<Set<string>>(new Set());

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors on cleanup
        }
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  // Clean up orphaned blob URLs when creatives list changes
  useEffect(() => {
    const currentBlobUrls = new Set(
      creatives
        .map(c => c.url)
        .filter(url => url.startsWith('blob:'))
    );

    // Remove any tracked URLs that are no longer in use
    blobUrlsRef.current.forEach(url => {
      if (!currentBlobUrls.has(url)) {
        removeBlobUrl(url);
      }
    });
  }, [creatives]);

  // Track blob URLs for cleanup
  const addBlobUrl = (url: string) => {
    if (blobManager) {
      blobManager.add(url);
    } else if (url.startsWith('blob:')) {
      blobUrlsRef.current.add(url);
    }
  };

  const removeBlobUrl = (url: string) => {
    if (blobManager) {
      blobManager.remove(url);
    } else if (url.startsWith('blob:')) {
      blobUrlsRef.current.delete(url);
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.warn('Failed to revoke blob URL:', e);
      }
    }
  };

  const getIconForType = (type: AdCreative['type']) => {
    switch (type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'audio':
        return <Music className="w-4 h-4" />;
      case 'html5':
        return <FileText className="w-4 h-4" />;
      case 'carousel':
        return <FileImage className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getFileType = (file: File): AdCreative['type'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.name.endsWith('.html') || file.name.endsWith('.zip')) return 'html5';
    return 'text';
  };

  // Get the asset type for backend upload (different from creative type)
  const getAssetTypeForUpload = (file: File): string => {
    // Use the existing asset types from the database
    // These are the actual types that exist in the content_assets table
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.includes('pdf')) return 'infographic';
    // For other types, default to 'page' which is a generic content type
    return 'page';
  };

  // Upload file to R2 storage with progress tracking
  const uploadFileToR2 = async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string | null> => {
    if (!contentId) {
      console.warn('No contentId provided, cannot upload to R2');
      return null;
    }

    // Starting upload process

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('content_id', contentId.toString());
      formData.append('asset_type', getAssetTypeForUpload(file));
      // Mark as supplementary since this is being uploaded from the ads interface
      formData.append('is_supplementary', '1');

      const response = await axios.post(`${apiBaseUrl}/api/assets/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (onProgress) {
              onProgress(percentCompleted);
            }
          }
        },
      });

      if (response.data && response.data.upload_url) {
        // If upload_url is relative, make it absolute
        const url = response.data.upload_url;
        if (url.startsWith('/')) {
          return `${apiBaseUrl}${url}`;
        }
        return url;
      } else if (response.data && response.data.id) {
        // If no upload_url, construct it from the asset ID
        return `${apiBaseUrl}/api/assets/${response.data.id}/download`;
      } else {
        throw new Error('No upload URL or asset ID returned');
      }
    } catch (error: any) {
      console.error('Failed to upload file to R2:', error);
      // Log more details about the error
      if (error.response) {
        console.error('Server error response:', error.response.data);
        console.error('Status:', error.response.status);
        console.error('Headers:', error.response.headers);
      }
      // Upload failed - parameters logged above
      return null;
    }
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || disabled) return;

    const newCreatives: AdCreative[] = [];

    for (let i = 0; i < Math.min(files.length, maxFiles - creatives.length); i++) {
      const file = files[i];
      const fileType = getFileType(file);

      // Create a temporary blob URL for immediate preview
      const tempBlobUrl = URL.createObjectURL(file);
      addBlobUrl(tempBlobUrl);

      const creative: AdCreative = {
        id: `${Date.now()}_${i}`,
        url: tempBlobUrl, // Start with blob URL for immediate preview
        type: fileType,
        name: file.name,
        size: file.size,
        uploading: true, // Mark as uploading
        uploadProgress: 0 // Initialize progress at 0
      };

      newCreatives.push(creative);

      // For video files, add additional metadata
      if (fileType === 'video') {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          creative.duration = Math.round(video.duration);
          // Update the creative with duration info
          onCreativesChange([...creatives, ...newCreatives.map(c =>
            c.id === creative.id ? { ...c, duration: creative.duration } : c
          )]);
        };
        video.onerror = (e) => {
          console.warn('Video metadata loading failed:', e);
        };
        video.src = tempBlobUrl;
      }

      // Upload to R2 in background with progress tracking
      uploadFileToR2(file, (progress) => {
        // Update progress
        creative.uploadProgress = progress;
        onCreativesChange([...creatives, ...newCreatives.map(c =>
          c.id === creative.id ? { ...c, uploadProgress: progress } : c
        )]);
      }).then(r2Url => {
        if (r2Url) {
          // Upload successful
          // Replace blob URL with R2 URL
          removeBlobUrl(tempBlobUrl);
          creative.url = r2Url;
          creative.uploading = false;
          creative.uploadProgress = 100;
          // Update the creative list
          onCreativesChange([...creatives, ...newCreatives.map(c =>
            c.id === creative.id ? { ...c, url: r2Url, uploading: false, uploadProgress: 100 } : c
          )]);
        } else {
          // Upload failed, mark as error
          creative.uploading = false;
          creative.uploadError = true;
          creative.uploadProgress = 0;
          onCreativesChange([...creatives, ...newCreatives.map(c =>
            c.id === creative.id ? { ...c, uploading: false, uploadError: true, uploadProgress: 0 } : c
          )]);
        }
      }).catch(error => {
        console.error('Upload failed:', error);
        creative.uploading = false;
        creative.uploadError = true;
        creative.uploadProgress = 0;
        onCreativesChange([...creatives, ...newCreatives.map(c =>
          c.id === creative.id ? { ...c, uploading: false, uploadError: true, uploadProgress: 0 } : c
        )]);
      });
    }

    onCreativesChange([...creatives, ...newCreatives]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const removeCreative = (id: string) => {
    if (disabled) return;
    const creative = creatives.find(c => c.id === id);
    if (creative) {
      // Use the tracked blob URL removal function
      removeBlobUrl(creative.url);
    }
    onCreativesChange(creatives.filter(c => c.id !== id));
  };

  const addCreativeFromUrl = () => {
    const url = prompt('Enter image/video URL:');
    if (url && !disabled) {
      const creative: AdCreative = {
        id: `url_${Date.now()}`,
        url,
        type: 'image', // Default, user can change
        name: `External Asset ${creatives.length + 1}`
      };
      onCreativesChange([...creatives, creative]);
    }
  };

  const updateCreative = (id: string, updates: Partial<AdCreative>) => {
    if (disabled) return;
    onCreativesChange(
      creatives.map(c => c.id === id ? { ...c, ...updates } : c)
    );
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-blue-400 bg-blue-50' :
          disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className={`w-8 h-8 mx-auto mb-2 ${disabled ? 'text-gray-400' : 'text-gray-500'}`} />
        <p className={`text-sm mb-2 ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
          {isDragging ? 'Drop files here' : 'Drag & drop files here or click to browse'}
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Supports: Images, Videos, Audio, HTML5 creatives
        </p>

        <div className="flex justify-center space-x-2">
          <label className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <Plus className="w-4 h-4 mr-2" />
            Upload Files
            <input
              type="file"
              multiple
              accept={acceptedTypes.join(',')}
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={disabled}
              className="hidden"
            />
          </label>

          <button
            onClick={addCreativeFromUrl}
            disabled={disabled}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Add from URL
          </button>
        </div>
      </div>

      {/* Uploaded Creatives */}
      {creatives.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Uploaded Creatives ({creatives.length}/{maxFiles})
          </h4>

          <div className="grid grid-cols-1 gap-3">
            {creatives.map((creative) => (
              <div key={creative.id} className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50">
                <div className="flex-shrink-0">
                  {getIconForType(creative.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={creative.name || ''}
                      onChange={(e) => updateCreative(creative.id!, { name: e.target.value })}
                      disabled={disabled}
                      className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                      placeholder="Asset name"
                    />
                    <select
                      value={creative.type}
                      onChange={(e) => updateCreative(creative.id!, { type: e.target.value as AdCreative['type'] })}
                      disabled={disabled}
                      className="text-xs border-gray-300 rounded px-2 py-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="audio">Audio</option>
                      <option value="html5">HTML5</option>
                      <option value="carousel">Carousel</option>
                      <option value="text">Text</option>
                    </select>
                  </div>

                  {(creative.size || creative.duration || creative.uploading || creative.uploadError) && (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">
                        {creative.uploading && !creative.uploadError && (
                          <span className="text-blue-600">• Uploading... {creative.uploadProgress || 0}%</span>
                        )}
                        {creative.uploadError && (
                          <span className="text-red-600">• Upload failed - using preview only</span>
                        )}
                        {!creative.uploading && !creative.uploadError && creative.size && typeof creative.size === 'number' && (
                          `${(creative.size / 1024 / 1024).toFixed(2)} MB`
                        )}
                        {creative.duration && ` • ${creative.duration}s`}
                        {creative.type === 'video' && creative.size && ` • Video`}
                      </p>

                      {/* Progress bar */}
                      {creative.uploading && !creative.uploadError && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${creative.uploadProgress || 0}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    type="text"
                    value={creative.altText || ''}
                    onChange={(e) => updateCreative(creative.id!, { altText: e.target.value })}
                    disabled={disabled}
                    placeholder="Alt text / description"
                    className="w-full text-xs text-gray-600 bg-transparent border-none p-0 mt-1 focus:outline-none focus:ring-0"
                  />
                </div>

                {creative.type === 'image' && (
                  <div className="flex-shrink-0">
                    <img
                      src={creative.url}
                      alt={creative.altText || creative.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  </div>
                )}

                {creative.type === 'video' && (
                  <div className="flex-shrink-0 relative">
                    <video
                      src={creative.url}
                      className="w-12 h-12 object-cover rounded border border-gray-300"
                      controls={false}
                      muted
                      preload="metadata"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        console.warn('Video preview error for:', creative.url, e);
                        // If this is a blob URL that failed, remove it from tracking
                        if (creative.url.startsWith('blob:')) {
                          removeBlobUrl(creative.url);
                        }
                        // Hide video and show fallback
                        e.currentTarget.style.display = 'none';
                        const container = e.currentTarget.parentElement;
                        const fallback = container?.querySelector('.video-fallback') as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      onLoadedData={(e) => {
                        // Show first frame as thumbnail
                        e.currentTarget.currentTime = 0.1;
                      }}
                      onCanPlay={(e) => {
                        // Video is ready to play, ensure it's visible
                        e.currentTarget.style.display = 'block';
                        const container = e.currentTarget.parentElement;
                        const fallback = container?.querySelector('.video-fallback') as HTMLElement;
                        if (fallback) fallback.style.display = 'none';
                      }}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Toggle play on click for small preview
                        const video = e.currentTarget;
                        if (video.paused) {
                          video.play().catch(err => console.log('Play failed:', err));
                        } else {
                          video.pause();
                        }
                      }}
                    />
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                      <Video className="w-4 h-4 text-white" />
                    </div>
                    {/* Fallback video icon */}
                    <div
                      className="video-fallback w-12 h-12 bg-gray-200 rounded flex items-center justify-center border border-gray-300"
                      style={{ display: 'none' }}
                    >
                      <Video className="w-6 h-6 text-gray-500" />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => removeCreative(creative.id!)}
                  disabled={disabled}
                  className={`flex-shrink-0 p-1 text-red-500 hover:text-red-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Platform-specific tips */}
      {platform && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>{platform} Tips:</strong>
            {platform === 'Google Ads' && ' Use high-quality images (1200x628 recommended). Videos should be under 30 seconds.'}
            {platform === 'Facebook & Instagram' && ' Square images (1080x1080) work best for feeds. Vertical videos (9:16) for Stories.'}
            {platform === 'LinkedIn Ads' && ' Professional imagery recommended. Use 1200x627 for sponsored content.'}
            {platform === 'Display & Programmatic' && ' Multiple banner sizes recommended. Keep file sizes under 150KB.'}
            {platform === 'Connected TV' && ' Use 1920x1080 MP4 videos. 15-30 second duration recommended.'}
            {platform === 'Radio & Audio' && ' MP3 format recommended. Include companion banners for visual elements.'}
          </p>
        </div>
      )}
    </div>
  );
}